/**
 * PostHog loader — runs ONLY after the user has explicitly accepted the cookie
 * banner. Anything more invasive than essential cookies (session replay,
 * autocapture, analytics) goes through this single gate.
 *
 * Lifecycle contract:
 *   loadPostHog()   — called when user clicks "Accept All". Lazy-loads the
 *                     PostHog SDK (snippet pattern), inits with session
 *                     recording enabled, and identifies the current user if any.
 *   stopPostHog()   — called when user clicks "Reject All" (mid-session) or
 *                     withdraws consent. Stops session recording and opts the
 *                     user out of all future capturing for THIS device.
 *
 * Why we don't import posthog-js as a module:
 *   The snippet pattern lets us avoid bundling ~200KB of analytics JS for users
 *   who reject. Network bytes only land if the user opted in.
 */
import logger from '../logger';

const POSTHOG_TOKEN = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_API_HOST = 'https://us.i.posthog.com';

let _loaded = false;

export function isPostHogLoaded() {
  return _loaded && !!window.posthog && typeof window.posthog.capture === 'function';
}

export function loadPostHog() {
  if (_loaded) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  try {
    // The PostHog snippet — copied from PostHog docs but invoked from JS so it
    // runs at consent-time, not page-load time.
    /* eslint-disable */
    !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
    /* eslint-enable */

    window.posthog.init(POSTHOG_TOKEN, {
      api_host: POSTHOG_API_HOST,
      person_profiles: 'identified_only',
      session_recording: { recordCrossOriginIframes: true },
      // Tell PostHog we already have consent — required so it doesn't apply
      // its own cookie-less mode in some configurations.
      opt_out_capturing_by_default: false,
      autocapture: true,
    });
    _loaded = true;
    logger.info('PostHog loaded after consent.');
  } catch (e) {
    logger.warn('PostHog load failed:', e);
  }
}

export function stopPostHog() {
  try {
    if (window.posthog && typeof window.posthog.stopSessionRecording === 'function') {
      window.posthog.stopSessionRecording();
    }
    if (window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
      window.posthog.opt_out_capturing();
    }
    if (window.posthog && typeof window.posthog.reset === 'function') {
      window.posthog.reset();
    }
    logger.info('PostHog stopped and opted out.');
  } catch (e) {
    logger.warn('PostHog stop failed:', e);
  }
}
